/// <amd-module name="Repos/Views/Import/Components/Clone/CloneWithCredential" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";

import { Button } from "VSSUI/Button";
import { ClipboardButton } from "VSSUI/Clipboard";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerOrientation, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

import { RepoProtocol } from "Repos/Common/Types";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";

import { generateCredential } from "Repos/Views/Import/Api";
import * as Resources from "Repos/Views/Import/Resources";

import { CloneWithCredentialProps } from "./CloneWithCredential.Props";

const validateAlias = new RegExp(/^[A-Za-z0-9]+$/);

export class CloneWithCredential extends VssComponent<CloneWithCredentialProps> {
    private altPassword = new ObservableValue<string | undefined>(undefined);
    private altUsername = new ObservableValue<string | undefined>(undefined);
    private emailName?: string;
    private generateInProgress = new ObservableValue<boolean>(false);
    private homePageUrl: string;
    private hostId: string;
    private httpClone?: string;
    private manageKeysUrl: string;
    private messageGenerateFailure = new ObservableValue<React.ReactNode | undefined>(undefined);
    private showCredentialForm = new ObservableValue<boolean>(false);
    private sshClone?: string;
    private supportsGenerate: boolean;
    private tokenUrl: string;

    public constructor(props: CloneWithCredentialProps, context: IVssComponentContext) {
        super(props, context);

        const vcViewModelDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const vcViewModel = vcViewModelDataProviderService.getViewModel();

        this.httpClone = vcViewModel.cloneUrl;
        this.sshClone = vcViewModel.sshUrl;

        // Get the general page data to determine whether or not we should offer to GenerateCredentials
        const pageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();
        if (pageData) {
            this.hostId = pageData.hostId;
            if (pageData.user) {
                const uniqueName = pageData.user.uniqueName;
                if (uniqueName) {
                    this.emailName = uniqueName.substring(0, uniqueName.lastIndexOf("@"));
                }
            }

            this.supportsGenerate = pageData.isHosted && userHasClaim(context.pageContext, UserClaims.Member);
        }

        // Get the urls for the keys and tokens used in the UI.
        const locationService = context.pageContext.getService<IVssLocationService>("IVssLocationService");
        this.homePageUrl = locationService.routeUrl("ms.vss-tfs-web.suite-me-page-route");
        this.manageKeysUrl = locationService.routeUrl("ms.vss-tfs-web.user-settings-keys-default-route");
        this.tokenUrl = locationService.routeUrl("ms.vss-tfs-web.user-settings-tokens-route");
    }

    public render() {
        return (
            <Observer
                failureMessage={this.messageGenerateFailure}
                generateInProgress={this.generateInProgress}
                protocol={this.props.protocol}
                showCredentialForm={this.showCredentialForm}
            >
                {(props: {
                    basicFieldsValid: number;
                    failureMessage: React.ReactNode;
                    generateInProgress: boolean;
                    protocol: RepoProtocol;
                    showCredentialForm: boolean;
                }) => (
                    <div className={css(this.props.className, "clone-with-credential rhythm-vertical-16")}>
                        <ScreenSizeObserver>
                            {(screenProps: { screenSize: ScreenSize }) => (
                                <div className={css(screenProps.screenSize === ScreenSize.xsmall ? "flex-column" : "flex-row flex-wrap")}>
                                    <div
                                        className={css(
                                            "clone-protocol-buttons flex-row flex-center",
                                            screenProps.screenSize === ScreenSize.xsmall ? "margin-bottom-4" : "margin-right-4"
                                        )}
                                    >
                                        {this.httpClone ? (
                                            <Button
                                                onClick={() => (this.props.protocol.value = RepoProtocol.https)}
                                                primary={props.protocol === RepoProtocol.https}
                                                text="HTTPS"
                                            />
                                        ) : null}
                                        {this.sshClone ? (
                                            <Button
                                                onClick={() => (this.props.protocol.value = RepoProtocol.ssh)}
                                                primary={props.protocol === RepoProtocol.ssh}
                                                text="SSH"
                                            />
                                        ) : null}
                                    </div>
                                    <div className="flex-row flex-center flex-grow">
                                        <TextField
                                            autoSelect={true}
                                            containerClassName="clone-url flex-grow"
                                            ariaLabelledBy={this.props.ariaLabelledBy}
                                            readOnly={true}
                                            spellCheck={false}
                                            value={props.protocol === RepoProtocol.https ? this.httpClone : this.sshClone}
                                        />
                                        <ClipboardButton
                                            className="margin-left-4"
                                            getContent={() => (props.protocol === RepoProtocol.https ? this.httpClone : this.sshClone) || ""}
                                            showCopiedTooltip={true}
                                            tooltipProps={{ text: Resources.CopyCloneToClipboard }}
                                        />
                                    </div>
                                </div>
                            )}
                        </ScreenSizeObserver>
                        {props.protocol === RepoProtocol.https && this.supportsGenerate ? (
                            props.failureMessage ? (
                                <MessageCard onDismiss={() => (this.messageGenerateFailure.value = undefined)} severity={MessageCardSeverity.Error}>
                                    <div className="flex-column body-xs">{props.failureMessage}</div>
                                </MessageCard>
                            ) : props.generateInProgress ? (
                                <Spinner label={Resources.GeneratingCredentials} orientation={SpinnerOrientation.row} size={SpinnerSize.large} />
                            ) : props.showCredentialForm && this.altPassword.value && this.altUsername.value ? (
                                    <div className="rhythm-vertical-16">
                                        <FormItem label={Resources.UsernameLabel}>
                                            <div className="flex-row flex-grow flex-center">
                                                <TextField
                                                    autoSelect={true}
                                                    containerClassName="flex-grow"
                                                    readOnly={true}
                                                    spellCheck={false}
                                                    value={this.altUsername}
                                                />
                                                <ClipboardButton
                                                    className="margin-left-4"
                                                    getContent={() => this.altUsername.value || ""}
                                                    showCopiedTooltip={true}
                                                    tooltipProps={{ text: Resources.CopyUsernameToClipboard }}
                                                />
                                            </div>
                                        </FormItem>
                                        <FormItem label={Resources.PasswordLabel}>
                                            <div className="flex-row flex-grow flex-center">
                                                <TextField
                                                    autoSelect={true}
                                                    containerClassName="flex-grow"
                                                    readOnly={true}
                                                    spellCheck={false}
                                                    value={this.altPassword}
                                                />
                                                <ClipboardButton
                                                    className="margin-left-4"
                                                    getContent={() => this.altPassword.value || ""}
                                                    showCopiedTooltip={true}
                                                    tooltipProps={{ text: Resources.CopyTokenToClipboard }}
                                                />
                                            </div>
                                            <MessageCard className="margin-top-8" severity={MessageCardSeverity.Warning}>
                                                <span className="body-xs">
                                                    <FormatComponent format={Resources.CopyPasswordToClipboard}>
                                                        <Link href={this.tokenUrl} target="_blank">
                                                            {Resources.LearnMore}
                                                        </Link>
                                                    </FormatComponent>
                                                </span>
                                            </MessageCard>
                                        </FormItem>
                                    </div>
                            ) : this.supportsGenerate ? (
                                <Button onClick={this.onGenerateCredential} text={Resources.GenerateCredentials} />
                            ) : null
                        ) : (
                            <div className="flex-row flex-center">
                                <Link className="inline-flex-row flex-center" href={this.manageKeysUrl}>
                                    <Icon className="icon-margin" iconName="LaptopSecure" />
                                    {Resources.ManageSSHKeys}
                                </Link>
                                <span className="margin-horizontal-4">|</span>
                                <Link href="https://go.microsoft.com/fwlink/?LinkID=715524" target="_blank">
                                    {Resources.LearnAboutSSH}
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </Observer>
        );
    }

    private onGenerateCredential = () => {
        generateCredential(this.context.pageContext, {
            description: format(Resources.TokenDescription, window.location.origin + this.homePageUrl),
            hostId: this.hostId
        }).then(
            result => {
                if (result) {
                    this.altPassword.value = result.Token;
                    this.altUsername.value = this.emailName;
                    this.showCredentialForm.value = true;
                }

                this.generateInProgress.value = false;
            },
            error => {
                this.messageGenerateFailure.value = error;
                this.generateInProgress.value = false;
            }
        );

        this.generateInProgress.value = true;
    };
}
